<script setup>
import { ref, computed, onMounted, onActivated, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useNexceoLeadLiveSync } from 'dashboard/composables/nexceo/useNexceoLeadLiveSync';
import leadsAPI from 'dashboard/api/nexceo/leads';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';
import nexceoKanbanStagesAPI from 'dashboard/api/nexceo/kanbanStages';
import LeadDetailsDrawer from './kanban/LeadDetailsDrawer.vue';
import LeadForm from './kanban/LeadForm.vue';

const store = useStore();

const { accountId } = useAccount();
const router = useRouter();

const leads = ref([]);
const projects = ref([]);
const stages = ref([]);
const agents = computed(() => store.getters['agents/getAgents']);

const isLoading = ref(false);
const loadError = ref('');
const selectedLead = ref(null);
const isDrawerOpen = ref(false);
const isLeadFormOpen = ref(false);

// Track which row's action menu is open
const openMenuLeadId = ref(null);

function toggleMenu(leadId) {
  openMenuLeadId.value = openMenuLeadId.value === leadId ? null : leadId;
}

function closeMenus() {
  openMenuLeadId.value = null;
}

function openLeadForm() {
  router.push({
    name: 'nexceo_leads_new',
    params: { accountId: accountId.value },
    query: { return_to: 'verified_leads', verified: 'true' },
  });
}

function openEditLead(lead) {
  closeMenus();
  router.push({
    name: 'nexceo_leads_edit',
    params: { accountId: accountId.value, id: lead.id },
    query: { return_to: 'verified_leads' },
  });
}

function closeLeadForm() {
  isLeadFormOpen.value = false;
}

// Filters
const search = ref('');
const filterProject = ref('');
const filterAgent = ref('');
const filterChannel = ref('');
const filterPriority = ref('');
const filterStage = ref('');
const filterVerification = ref('');
const filterStartDate = ref('');
const filterEndDate = ref('');
const showFilters = ref(false);

const activeFilterCount = computed(
  () =>
    [
      filterAgent.value,
      filterChannel.value,
      filterPriority.value,
      filterStage.value,
      filterVerification.value,
      filterStartDate.value,
      filterEndDate.value,
    ].filter(Boolean).length
);

const channelOptions = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'web_widget', label: 'Website' },
  { value: 'api', label: 'API / Manual' },
];
const priorityOptions = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];
const verificationOptions = [
  { value: 'ai_verified', label: 'AI Verified' },
  { value: 'human_verified', label: 'Human Verified' },
];

async function loadData() {
  try {
    const [projectsRes, stagesRes] = await Promise.all([
      nexceoProjectOptionsAPI.get(),
      nexceoKanbanStagesAPI.getAll(),
    ]);
    projects.value = projectsRes.data?.projects ?? [];
    stages.value = Array.isArray(stagesRes.data)
      ? stagesRes.data
      : (stagesRes.data?.stages ?? []);
  } catch {
    // non-critical
  }
}

async function loadLeads() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const params = {};
    if (filterProject.value) params.project_id = filterProject.value;
    if (filterAgent.value) params.agent_id = filterAgent.value;
    if (filterChannel.value) params.source_channel = filterChannel.value;
    if (filterPriority.value) params.priority = filterPriority.value;
    if (filterStage.value) params.kanban_stage_id = filterStage.value;
    if (filterVerification.value)
      params.verification_status = filterVerification.value;
    if (filterStartDate.value) params.start_date = filterStartDate.value;
    if (filterEndDate.value) params.end_date = filterEndDate.value;
    if (search.value.trim()) params.search = search.value.trim();

    const { data } = await leadsAPI.getVerifiedLeads(params);
    leads.value = data;
  } catch {
    loadError.value = 'Failed to load verified leads.';
  } finally {
    isLoading.value = false;
  }
}

watch(
  [
    filterProject,
    filterAgent,
    filterChannel,
    filterPriority,
    filterStage,
    filterVerification,
    filterStartDate,
    filterEndDate,
  ],
  loadLeads
);

// Debounce search
let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(loadLeads, 350);
});

// M26.15.7 — shared with AllLeadsPage.vue (see the composable's own
// comment for why this page keeps its own paginated `leads` ref instead of
// the nexceoLeads Vuex store).
useNexceoLeadLiveSync(leads, selectedLead);

onMounted(async () => {
  await Promise.all([loadData(), loadLeads(), store.dispatch('agents/get')]);
});

// Re-fetch when returning from Add/Edit Lead page (keep-alive activation)
onActivated(() => {
  loadLeads();
});

function openDrawer(lead) {
  selectedLead.value = lead;
  isDrawerOpen.value = true;
}

async function onDrawerUpdated() {
  await loadLeads();
  // Re-sync the open drawer's lead from the freshly loaded list
  if (selectedLead.value) {
    const refreshed = leads.value.find(l => l.id === selectedLead.value.id);
    if (refreshed) selectedLead.value = refreshed;
  }
}

function onFormSaved() {
  closeLeadForm();
  loadLeads();
}

function clearFilters() {
  filterAgent.value = '';
  filterChannel.value = '';
  filterPriority.value = '';
  filterStage.value = '';
  filterVerification.value = '';
  filterStartDate.value = '';
  filterEndDate.value = '';
}

// Inline verify / reject from table
const actioningId = ref(null);

// Site visit form (schedule from table row)
const isVisitFormOpen = ref(null); // holds the lead to schedule for
const visitFormPrefillType = ref('site_visit');
import SiteVisitForm from './calendar/SiteVisitForm.vue';
import siteVisitsAPI from 'dashboard/api/nexceo/siteVisits';

async function onVerify(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.verify(lead.id);
    useAlert('Lead marked as verified.');
    loadLeads();
  } catch {
    useAlert('Failed to verify lead.');
  } finally {
    actioningId.value = null;
  }
}

async function onReject(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.reject(lead.id, 'Rejected from Verified Leads page');
    useAlert('Lead rejected.');
    loadLeads();
  } catch {
    useAlert('Failed to reject lead.');
  } finally {
    actioningId.value = null;
  }
}

async function onMarkLost(lead) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.markLost(lead.id, 'Marked lost from Verified Leads');
    useAlert('Lead marked as lost.');
    loadLeads();
  } catch {
    useAlert('Failed to mark lead as lost.');
  } finally {
    actioningId.value = null;
  }
}

async function onSendTeamAlert(lead, force = false) {
  closeMenus();
  actioningId.value = lead.id;
  try {
    await leadsAPI.sendTeamAlert(lead.id, { force });
    useAlert('Team alert sent to project team members.');
  } catch (err) {
    const msg =
      err?.response?.data?.message || err?.response?.data?.error || '';
    if (!force && msg.includes('already sent')) {
      if (window.confirm('Alert already sent. Resend to all team members?')) {
        await onSendTeamAlert(lead, true);
      }
      return;
    }
    useAlert(msg || 'Failed to send team alert.');
  } finally {
    actioningId.value = null;
  }
}

function openScheduleVisit(lead, type = 'site_visit') {
  closeMenus();
  visitFormPrefillType.value = type;
  isVisitFormOpen.value = lead;
}

async function resendVisitConfirmation(lead) {
  closeMenus();
  if (!lead.next_site_visit_id) return;
  try {
    await siteVisitsAPI.resendConfirmation(lead.next_site_visit_id);
    useAlert('Confirmation resent to customer.');
  } catch {
    useAlert('Failed to resend confirmation.');
  }
}

function onVisitSaved() {
  isVisitFormOpen.value = null;
  loadLeads();
}

async function onMoveStage(lead, stageKey) {
  try {
    await leadsAPI.moveStage(lead.id, stageKey);
    loadLeads();
  } catch {
    useAlert('Failed to move stage.');
  }
}

// Helpers
function formatBudget(lead) {
  const sym = lead.currency === 'INR' ? '₹' : lead.currency || '';
  const fmt = v => {
    if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
    if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
    return Number(v).toLocaleString();
  };
  if (lead.budget_max && lead.budget_min)
    return `${sym}${fmt(lead.budget_min)}–${sym}${fmt(lead.budget_max)}`;
  if (lead.budget_max || lead.budget_min)
    return `${sym}${fmt(lead.budget_max || lead.budget_min)}`;
  return '—';
}

function requirementSummary(lead) {
  const p = [];
  if (lead.bedrooms) p.push(lead.bedrooms);
  if (lead.preferred_location) p.push(lead.preferred_location);
  const b = formatBudget(lead);
  if (b !== '—') p.push(b);
  return p.join(' · ') || '—';
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
}

function verificationBadge(status) {
  const map = {
    ai_verified: {
      cls: 'bg-woot-100 text-woot-700',
      icon: 'i-lucide-bot',
      label: 'AI Verified',
    },
    human_verified: {
      cls: 'bg-green-100 text-green-700',
      icon: 'i-lucide-user-check',
      label: 'Human Verified',
    },
    unverified: {
      cls: 'bg-slate-100 text-slate-500',
      icon: 'i-lucide-clock',
      label: 'Unverified',
    },
    rejected: {
      cls: 'bg-red-100 text-red-600',
      icon: 'i-lucide-x-circle',
      label: 'Rejected',
    },
  };
  return map[status] || map.unverified;
}

function priorityBadge(p) {
  const map = {
    urgent: 'bg-red-100 text-red-700',
    high: 'bg-yellow-100 text-yellow-700',
    normal: 'bg-woot-100 text-woot-600',
    low: 'bg-slate-100 text-slate-500',
  };
  return map[p] || map.normal;
}

const otherStagesFor = lead =>
  stages.value.filter(s => s.id !== lead.kanban_stage_id);

function openWorkspace(lead) {
  router.push({
    name: 'nexceo_lead_workspace',
    params: { accountId: accountId.value, id: lead.id },
  });
}

function openQuickPreview(lead) {
  closeMenus();
  openDrawer(lead);
}

// "My Work" quick-view tabs — computed client-side over the already-loaded,
// already-filtered `leads` array (real API data, no fabricated counters).
const activeView = ref('all');

function isDueToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function isOverdue(dateStr) {
  return !!dateStr && new Date(dateStr) < new Date() && !isDueToday(dateStr);
}

function isUpcoming(dateStr) {
  return !!dateStr && new Date(dateStr) > new Date() && !isDueToday(dateStr);
}

function isAwaitingReply(lead) {
  if (!lead.last_customer_message_at) return false;
  const customerAt = new Date(lead.last_customer_message_at).getTime();
  const agentAt = lead.last_agent_message_at
    ? new Date(lead.last_agent_message_at).getTime()
    : 0;
  const aiAt = lead.last_ai_message_at
    ? new Date(lead.last_ai_message_at).getTime()
    : 0;
  return customerAt > agentAt && customerAt > aiAt;
}

const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'new', label: 'New' },
  { key: 'hot', label: 'Hot' },
  { key: 'site_visits', label: 'Site Visits' },
  { key: 'awaiting_reply', label: 'Awaiting Reply' },
];

function matchesView(lead, view) {
  switch (view) {
    case 'today':
      return isDueToday(lead.next_follow_up_at) || isDueToday(lead.next_site_visit_at);
    case 'overdue':
      return isOverdue(lead.next_follow_up_at);
    case 'upcoming':
      return isUpcoming(lead.next_follow_up_at) || isUpcoming(lead.next_site_visit_at);
    case 'new': {
      const created = new Date(lead.created_at).getTime();
      return Date.now() - created < 24 * 60 * 60 * 1000;
    }
    case 'hot':
      return ['urgent', 'high'].includes(lead.priority);
    case 'site_visits':
      return !!lead.next_site_visit_at;
    case 'awaiting_reply':
      return isAwaitingReply(lead);
    default:
      return true;
  }
}

const viewCounts = computed(() => {
  const counts = {};
  VIEWS.forEach(v => {
    counts[v.key] = v.key === 'all' ? leads.value.length : leads.value.filter(l => matchesView(l, v.key)).length;
  });
  return counts;
});

const filteredByView = computed(() =>
  activeView.value === 'all'
    ? leads.value
    : leads.value.filter(l => matchesView(l, activeView.value))
);

const kpis = computed(() => [
  {
    key: 'new',
    label: 'New Leads',
    value: viewCounts.value.new,
    icon: 'i-lucide-user-plus',
    cls: 'bg-woot-50 dark:bg-woot-500/10 text-woot-600',
  },
  {
    key: 'today',
    label: 'Follow-ups Due',
    value: viewCounts.value.today + viewCounts.value.overdue,
    icon: 'i-lucide-phone-call',
    cls: 'bg-green-50 dark:bg-green-500/10 text-green-600',
  },
  {
    key: 'site_visits',
    label: 'Site Visits',
    value: viewCounts.value.site_visits,
    icon: 'i-lucide-calendar-check',
    cls: 'bg-violet-50 dark:bg-violet-500/10 text-violet-600',
  },
  {
    key: 'hot',
    label: 'Hot Leads',
    value: viewCounts.value.hot,
    icon: 'i-lucide-flame',
    cls: 'bg-red-50 dark:bg-red-500/10 text-red-600',
  },
  {
    key: 'awaiting_reply',
    label: 'Awaiting Reply',
    value: viewCounts.value.awaiting_reply,
    icon: 'i-lucide-clock',
    cls: 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-600',
  },
]);

// Right-rail "Today's Schedule" — real site visits happening today, derived
// from the already-loaded leads (next_site_visit_at), sorted chronologically.
const todaysScheduleVisits = computed(() =>
  leads.value
    .filter(l => isDueToday(l.next_site_visit_at))
    .sort((a, b) => new Date(a.next_site_visit_at) - new Date(b.next_site_visit_at))
);

function formatSiteVisitTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <span class="i-lucide-badge-check size-4 text-woot-500 shrink-0" />
      <h1 class="text-base font-semibold text-n-slate-12">My Work</h1>
      <span
        class="text-xs text-n-slate-7 bg-n-alpha-2 px-2 py-0.5 rounded-full"
      >
        {{ leads.length }} verified leads
      </span>

      <div class="flex items-center gap-2 ml-auto shrink-0">
        <!-- Search -->
        <div class="relative hidden sm:block">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
          />
          <input
            v-model="search"
            type="text"
            placeholder="Search name, phone or email…"
            class="pl-8 pr-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-woot-500 w-44 lg:w-56 transition-all"
          />
        </div>

        <!-- Filter toggle -->
        <button
          class="relative flex items-center justify-center w-8 h-8 rounded-lg border transition-colors"
          :class="
            showFilters
              ? 'bg-woot-500 border-woot-500 text-white'
              : 'border-n-weak bg-n-background text-n-slate-8 hover:border-woot-400 hover:text-woot-500'
          "
          title="Filters"
          @click="showFilters = !showFilters"
        >
          <span class="i-lucide-sliders-horizontal size-3.5" />
          <span
            v-if="activeFilterCount"
            class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-ruby-500 text-white text-[9px] font-bold flex items-center justify-center"
          >
            {{ activeFilterCount }}</span
          >
        </button>

        <!-- Add Lead -->
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors font-medium"
          @click.prevent.stop="openLeadForm"
        >
          <span class="i-lucide-plus size-4" />
          <span class="hidden sm:inline">Add Lead</span>
        </button>
      </div>
    </div>

    <!-- KPI strip (real, computed client-side from the loaded leads — no fabricated counters) -->
    <div
      v-if="leads.length"
      class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 px-4 py-3 border-b border-n-weak shrink-0 bg-n-background"
    >
      <div
        v-for="kpi in kpis"
        :key="kpi.key"
        class="flex items-center gap-2.5 rounded-xl border border-n-weak p-2.5 cursor-pointer hover:border-woot-300 transition-colors"
        @click="activeView = kpi.key"
      >
        <div
          class="flex items-center justify-center size-8 rounded-lg shrink-0"
          :class="kpi.cls"
        >
          <span :class="kpi.icon" class="size-4" />
        </div>
        <div class="min-w-0">
          <p class="text-lg font-semibold text-n-slate-12 leading-tight">
            {{ kpi.value }}
          </p>
          <p class="text-[11px] text-n-slate-8 truncate">{{ kpi.label }}</p>
        </div>
      </div>
    </div>

    <!-- Quick-view tabs + project filter (same row) -->
    <div
      class="flex items-center justify-between gap-3 px-4 py-2 border-b border-n-weak shrink-0 bg-n-background"
    >
      <div class="flex items-center gap-1 overflow-x-auto">
        <button
          v-for="v in VIEWS"
          :key="v.key"
          class="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors"
          :class="
            activeView === v.key
              ? 'bg-woot-500 text-white'
              : 'text-n-slate-9 hover:bg-n-alpha-2'
          "
          @click="activeView = v.key"
        >
          {{ v.label }} ({{ viewCounts[v.key] }})
        </button>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <span class="text-xs text-n-slate-8 shrink-0">Project:</span>
        <select
          v-model="filterProject"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
        >
          <option value="">All Projects</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Advanced filters -->
    <transition name="slide-down">
      <div
        v-if="showFilters"
        class="flex flex-wrap items-center gap-2 px-4 py-2 bg-n-background border-b border-n-weak shrink-0"
      >
        <span class="text-xs font-medium text-n-slate-9">Filters:</span>

        <select v-model="filterAgent" class="nexceo-filter-select">
          <option value="">All Agents</option>
          <option v-for="a in agents" :key="a.id" :value="a.id">
            {{ a.name }}
          </option>
        </select>

        <select v-model="filterChannel" class="nexceo-filter-select">
          <option value="">All Channels</option>
          <option v-for="c in channelOptions" :key="c.value" :value="c.value">
            {{ c.label }}
          </option>
        </select>

        <select v-model="filterPriority" class="nexceo-filter-select">
          <option value="">All Priorities</option>
          <option v-for="p in priorityOptions" :key="p.value" :value="p.value">
            {{ p.label }}
          </option>
        </select>

        <select v-model="filterStage" class="nexceo-filter-select">
          <option value="">All Stages</option>
          <option v-for="s in stages" :key="s.id" :value="s.id">
            {{ s.name }}
          </option>
        </select>

        <select v-model="filterVerification" class="nexceo-filter-select">
          <option value="">All Verified</option>
          <option
            v-for="v in verificationOptions"
            :key="v.value"
            :value="v.value"
          >
            {{ v.label }}
          </option>
        </select>

        <input
          v-model="filterStartDate"
          type="date"
          class="nexceo-filter-select"
          title="From date"
        />
        <input
          v-model="filterEndDate"
          type="date"
          class="nexceo-filter-select"
          title="To date"
        />

        <button
          v-if="activeFilterCount"
          class="text-xs text-ruby-600 hover:text-ruby-700 font-medium px-2 py-1.5 rounded-lg hover:bg-ruby-50 transition-colors"
          @click="clearFilters"
        >
          Clear
        </button>
      </div>
    </transition>

    <!-- Error banner -->
    <div
      v-if="loadError"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 text-sm text-ruby-700 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ loadError }}
      <button class="underline ml-1 font-medium" @click="loadLeads">
        Retry
      </button>
    </div>

    <div class="flex-1 flex overflow-hidden">
    <div class="flex-1 min-w-0 flex flex-col overflow-hidden">
    <!-- Loading -->
    <div
      v-if="isLoading && leads.length === 0"
      class="flex-1 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
        <span class="text-sm">Loading verified leads…</span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!isLoading && leads.length === 0"
      class="flex-1 flex flex-col items-center justify-center gap-5"
    >
      <div
        class="flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
      >
        <span class="i-lucide-badge-check size-7 text-woot-500" />
      </div>
      <div class="text-center max-w-sm">
        <p class="text-base font-semibold text-n-slate-12">
          No verified leads yet
        </p>
        <p class="text-sm text-n-slate-9 mt-1">
          Leads appear here when the AI or an agent verifies them. You can also
          manually add a verified lead using the
          <strong>Add Lead</strong> button.
        </p>
      </div>
    </div>

    <!-- No results for the active quick-view -->
    <div
      v-else-if="!filteredByView.length"
      class="flex-1 flex flex-col items-center justify-center gap-2"
    >
      <span class="i-lucide-inbox size-6 text-n-slate-6" />
      <p class="text-sm text-n-slate-9">
        No leads in "{{ VIEWS.find(v => v.key === activeView)?.label }}".
      </p>
      <button class="text-xs text-woot-600 hover:underline" @click="activeView = 'all'">
        Show all
      </button>
    </div>

    <!-- Table -->
    <div v-else class="flex-1 overflow-auto">
      <table class="w-full text-sm border-collapse min-w-[1100px]">
        <thead class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
          <tr>
            <th
              class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Lead
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Mobile
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Project
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Requirement
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Priority
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Stage
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Verified
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Agent
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Created
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Follow-up
            </th>
            <th
              class="text-left px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap"
            >
              Visit
            </th>
            <th
              class="px-3 py-3 text-xs font-semibold text-n-slate-9 whitespace-nowrap text-right"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="lead in filteredByView"
            :key="lead.id"
            class="border-b border-n-weak hover:bg-n-alpha-1 dark:hover:bg-n-solid-2 transition-colors cursor-pointer"
            @click="openWorkspace(lead)"
          >
            <!-- Lead name + channel -->
            <td class="px-4 py-3">
              <div class="font-medium text-n-slate-12 leading-tight">
                {{ lead.name || 'Unnamed' }}
              </div>
              <div
                v-if="lead.email"
                class="text-xs text-n-slate-11 truncate max-w-[160px]"
              >
                {{ lead.email }}
              </div>
              <div class="flex items-center gap-1 mt-0.5">
                <span
                  class="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
                >
                  {{ lead.source_channel || '—' }}
                </span>
              </div>
            </td>

            <!-- Mobile -->
            <td class="px-3 py-3 text-n-slate-12 whitespace-nowrap">
              {{ lead.phone_number || '—' }}
            </td>

            <!-- Project -->
            <td class="px-3 py-3">
              <span class="text-n-slate-12">{{
                lead.project?.name || '—'
              }}</span>
            </td>

            <!-- Requirement -->
            <td class="px-3 py-3 text-xs text-n-slate-12 max-w-[180px]">
              {{ requirementSummary(lead) }}
            </td>

            <!-- Priority -->
            <td class="px-3 py-3">
              <span
                :class="priorityBadge(lead.priority)"
                class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold capitalize"
              >
                {{ lead.priority }}
              </span>
            </td>

            <!-- Kanban stage -->
            <td class="px-3 py-3">
              <div class="flex items-center gap-1">
                <span class="text-xs text-n-slate-12">{{
                  lead.kanban_stage?.name || '—'
                }}</span>
              </div>
            </td>

            <!-- Verification badge -->
            <td class="px-3 py-3">
              <span
                :class="verificationBadge(lead.verification_status).cls"
                class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
              >
                <span
                  :class="verificationBadge(lead.verification_status).icon"
                  class="size-3"
                />
                {{ verificationBadge(lead.verification_status).label }}
              </span>
            </td>

            <!-- Agent -->
            <td class="px-3 py-3 text-xs text-n-slate-12">
              {{ lead.assigned_agent?.name || '—' }}
            </td>

            <!-- Created date -->
            <td class="px-3 py-3 text-xs text-n-slate-12 whitespace-nowrap">
              {{ formatDate(lead.created_at) }}
            </td>

            <!-- Next follow-up -->
            <td class="px-3 py-3 text-xs text-n-slate-12 whitespace-nowrap">
              {{ formatDate(lead.next_follow_up_at) }}
            </td>

            <!-- Site visit status + time -->
            <td class="px-3 py-3">
              <div class="flex items-center gap-1">
                <span
                  :class="
                    lead.site_visit_status === 'not_scheduled'
                      ? 'text-n-slate-7'
                      : lead.next_site_visit_type === 'video_call'
                        ? 'text-woot-600 font-medium'
                        : 'text-green-600 font-medium'
                  "
                  class="text-xs capitalize"
                >
                  <template
                    v-if="
                      lead.site_visit_status !== 'not_scheduled' &&
                      lead.next_site_visit_type === 'video_call'
                    "
                  >
                    Video Call
                  </template>
                  <template v-else>
                    {{
                      (lead.site_visit_status || 'not_scheduled').replace(
                        '_',
                        ' '
                      )
                    }}
                  </template>
                </span>
                <span
                  v-if="
                    lead.next_site_visit_type === 'video_call' &&
                    lead.site_visit_status !== 'not_scheduled'
                  "
                  class="i-lucide-video size-3 text-woot-500"
                />
              </div>
              <span
                v-if="lead.next_site_visit_at"
                class="block text-xs text-n-slate-11 mt-0.5 flex items-center gap-0.5"
              >
                <span class="i-lucide-calendar size-3 shrink-0" />
                {{ formatDate(lead.next_site_visit_at) }}
              </span>
              <a
                v-if="lead.next_site_visit_meeting_url"
                :href="lead.next_site_visit_meeting_url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-0.5 text-xs text-woot-500 hover:underline mt-0.5"
                @click.stop
              >
                <span class="i-lucide-video size-3" />
                Join Meet
              </a>
            </td>

            <!-- Actions dropdown -->
            <td class="px-3 py-3" @click.stop>
              <div class="relative flex items-center justify-end">
                <button
                  class="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-n-slate-9 border border-n-weak rounded-lg hover:border-woot-400 hover:text-woot-600 transition-colors"
                  :disabled="actioningId === lead.id"
                  @click="toggleMenu(lead.id)"
                >
                  <span
                    v-if="actioningId === lead.id"
                    class="i-lucide-loader-2 size-3 animate-spin"
                  />
                  <span v-else>Actions</span>
                  <span class="i-lucide-chevron-down size-3" />
                </button>

                <!-- Dropdown panel -->
                <div
                  v-if="openMenuLeadId === lead.id"
                  class="absolute right-0 top-full mt-1 z-20 w-52 bg-white dark:bg-n-solid-3 border border-n-weak rounded-xl shadow-lg py-1 text-sm"
                >
                  <!-- Open Workspace -->
                  <button
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                    @click="
                      openWorkspace(lead);
                      closeMenus();
                    "
                  >
                    <span class="i-lucide-layout-dashboard size-4 text-woot-500" />
                    Open Workspace
                  </button>

                  <!-- Quick Preview (drawer) -->
                  <button
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                    @click="openQuickPreview(lead)"
                  >
                    <span class="i-lucide-eye size-4 text-n-slate-8" />
                    Quick Preview
                  </button>

                  <!-- Edit Lead -->
                  <button
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                    @click="openEditLead(lead)"
                  >
                    <span class="i-lucide-pencil size-4 text-woot-500" />
                    Edit Lead
                  </button>

                  <!-- Schedule Visit / Meeting -->
                  <div class="border-t border-n-weak/60 pt-1 mt-1">
                    <p
                      class="px-4 pt-1 pb-0.5 text-[10px] font-semibold text-n-slate-7 uppercase tracking-wider"
                    >
                      Schedule
                    </p>
                    <button
                      class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                      @click="openScheduleVisit(lead, 'site_visit')"
                    >
                      <span class="i-lucide-map-pin size-4 text-green-600" />
                      Site Visit
                    </button>
                    <button
                      class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                      @click="openScheduleVisit(lead, 'video_call')"
                    >
                      <span class="i-lucide-video size-4 text-woot-500" />
                      Google Meet
                    </button>
                    <button
                      class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                      @click="openScheduleVisit(lead, 'office_meeting')"
                    >
                      <span class="i-lucide-building-2 size-4 text-n-slate-8" />
                      Agent Follow Up
                    </button>
                    <button
                      v-if="lead.next_site_visit_id"
                      class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                      @click="resendVisitConfirmation(lead)"
                    >
                      <span class="i-lucide-send size-4 text-n-slate-7" />
                      Resend Confirmation
                    </button>
                  </div>

                  <!-- Move Stage -->
                  <div class="px-2 pt-1 pb-1">
                    <select
                      class="w-full text-xs text-n-slate-9 bg-n-alpha-1 border border-n-weak rounded-lg px-2 py-1.5 outline-none hover:border-woot-400 cursor-pointer"
                      @change="
                        e => {
                          if (e.target.value) {
                            onMoveStage(lead, e.target.value);
                            closeMenus();
                            e.target.value = '';
                          }
                        }
                      "
                    >
                      <option value="" disabled selected>Move Stage…</option>
                      <option
                        v-for="s in otherStagesFor(lead)"
                        :key="s.id"
                        :value="s.key"
                      >
                        {{ s.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Open Chatwoot conversation -->
                  <a
                    v-if="lead.conversation_id"
                    :href="`/app/accounts/${accountId}/conversations/${lead.conversation_id}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                    @click.stop="closeMenus()"
                  >
                    <span
                      class="i-lucide-external-link size-4 text-n-slate-8"
                    />
                    Open Conversation
                  </a>

                  <div class="border-t border-n-weak my-1" />

                  <!-- Verify -->
                  <button
                    v-if="
                      lead.verification_status === 'unverified' ||
                      lead.verification_status === 'ai_verified'
                    "
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-green-700 hover:bg-green-50 transition-colors"
                    @click="onVerify(lead)"
                  >
                    <span class="i-lucide-badge-check size-4" />
                    Mark as Verified
                  </button>

                  <!-- Mark Lost -->
                  <button
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-yellow-700 hover:bg-yellow-50 transition-colors"
                    @click="onMarkLost(lead)"
                  >
                    <span class="i-lucide-thumbs-down size-4" />
                    Mark Lost
                  </button>

                  <!-- Send Team Alert -->
                  <button
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-woot-700 hover:bg-woot-50 transition-colors"
                    @click="onSendTeamAlert(lead)"
                  >
                    <span class="i-lucide-bell-ring size-4" />
                    Send Team Alert
                  </button>

                  <!-- Reject -->
                  <button
                    v-if="lead.verification_status !== 'rejected'"
                    class="flex items-center gap-2.5 w-full px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                    @click="onReject(lead)"
                  >
                    <span class="i-lucide-x-circle size-4" />
                    Reject Lead
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>

    <!-- Right rail: real data only (Today's Schedule from actual site visits,
         Quick Actions reusing existing functions) — no AI Suggestions panel,
         since that would require fabricating AI-generated insights. -->
    <aside
      v-if="leads.length"
      class="hidden xl:flex flex-col w-72 shrink-0 border-l border-n-weak bg-n-background overflow-y-auto p-4 gap-4"
    >
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-n-slate-12">Today's Schedule</h3>
        </div>
        <div v-if="!todaysScheduleVisits.length" class="text-xs text-n-slate-8">
          No site visits scheduled today.
        </div>
        <ul v-else class="space-y-2.5">
          <li
            v-for="lead in todaysScheduleVisits"
            :key="lead.id"
            class="rounded-lg border border-n-weak p-2.5 cursor-pointer hover:border-woot-300"
            @click="openWorkspace(lead)"
          >
            <p class="text-xs font-semibold text-n-slate-12">
              {{ formatSiteVisitTime(lead.next_site_visit_at) }}
            </p>
            <p class="text-xs text-n-slate-9 truncate">{{ lead.name || 'Unnamed' }}</p>
            <p class="text-[11px] text-n-slate-8 truncate">{{ lead.project?.name || '—' }}</p>
          </li>
        </ul>
      </div>

      <div>
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Quick Actions</h3>
        <div class="grid grid-cols-2 gap-2">
          <button
            class="flex flex-col items-center gap-1 rounded-lg border border-n-weak p-2.5 text-xs text-n-slate-11 hover:border-woot-400"
            @click="openLeadForm"
          >
            <span class="i-lucide-user-plus size-4 text-woot-600" />
            Add Lead
          </button>
          <router-link
            :to="{ name: 'nexceo_calendar' }"
            class="flex flex-col items-center gap-1 rounded-lg border border-n-weak p-2.5 text-xs text-n-slate-11 hover:border-woot-400"
          >
            <span class="i-lucide-calendar size-4 text-violet-600" />
            View Calendar
          </router-link>
        </div>
      </div>
    </aside>
    </div>
  </div>

  <!-- Lead details drawer -->
  <LeadDetailsDrawer
    :lead="selectedLead"
    :is-open="isDrawerOpen"
    :stages="stages"
    :agents="agents"
    @close="
      isDrawerOpen = false;
      selectedLead = null;
    "
    @updated="onDrawerUpdated"
  />

  <!-- Add lead form (auto-marks human_verified) -->
  <LeadForm
    :lead="null"
    :is-open="isLeadFormOpen"
    :projects="projects"
    :stages="stages"
    :agents="agents"
    auto-verify
    @close="closeLeadForm"
    @saved="onFormSaved"
  />

  <!-- Schedule site visit / meeting form -->
  <SiteVisitForm
    :is-open="!!isVisitFormOpen"
    :prefill-lead="isVisitFormOpen"
    :prefill-visit-type="visitFormPrefillType"
    @close="isVisitFormOpen = null"
    @saved="onVisitSaved"
  />

  <!-- Click-outside overlay to close action menus -->
  <div v-if="openMenuLeadId" class="fixed inset-0 z-10" @click="closeMenus" />
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.15s ease;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 80px;
}

.nexceo-filter-select {
  @apply text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500;
}
</style>
